import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Hidden,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { User, Group } from "../users/users-types";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Delete,
} from "@mui/icons-material";
import DeleteElementFromTable from "../reusable/DeleteElementFromTableDialog";
import { GroupRounded } from "@mui/icons-material";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import { useTranslation } from "react-i18next";
import { grey } from "@mui/material/colors";
import AddGroupDialog from "./AddGroupDialog";
import Edit from "@mui/icons-material/Edit";
import ErrorComponent from "../reusable/ErrorComponent";

function GroupsTable(props: {
  columns: string[];
  groups: Group[];
  users: User[];
  isLoading: boolean;
  isError: any;
}) {
  const { t } = useTranslation();

  const groupColumns = [t("Name"), t("Members"), t(""), t("")];

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const skeletonArray = Array(5).fill("");

  const [openUpdateGroupDialog, setOpenUpdateGroupDialog] =
    React.useState(false);

  const [selectedGroup, setSelectedGroup] = React.useState<Group>();

  const handleOpen = (group: Group) => {
    setSelectedGroup(group);
    setOpenUpdateGroupDialog(true);
  };

  const [openDelete, setOpenDelete] = useState(false);
  const [deletedGroup, setDeletedGroup] = useState<Group | undefined>(
    undefined
  );

  const title = [t("Edit"), t("Delete"), t("See users"), t("Close")];

  const handleSetDeleteInfo = (element: Group) => {
    setOpenDelete(true);
    setDeletedGroup(element);
  };

  function CustomTableRow(props: { rowElement: Group; groups: Group[] }) {
    const [open, setOpen] = React.useState(false);

    return (
      <React.Fragment>
        <TableRow
          key={props.rowElement.id}
          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
        >
          <TableCell key="open" align="center" width="20%">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <Tooltip title={title[3]}>
                  <KeyboardArrowUp />
                </Tooltip>
              ) : (
                <Tooltip title={title[2]}>
                  <KeyboardArrowDown />
                </Tooltip>
              )}
            </IconButton>
          </TableCell>
          <TableCell
            component="th"
            scope="row"
            align="center"
            key={props.rowElement.name}
          >
            {props.rowElement.name}
          </TableCell>
          <TableCell align="center" key={props.rowElement.userCount}>
            {props.rowElement.userCount}
          </TableCell>
          {localStorage.getItem("Role") !== "USER" && (
            <Hidden smDown>
              <TableCell align="center" key={props.rowElement.id + "-edit"}>
                <Tooltip title={title[0]}>
                  <Button onClick={() => handleOpen(props.rowElement)}>
                    <Edit color="action" />
                  </Button>
                </Tooltip>
              </TableCell>
              <TableCell align="center" key={props.rowElement.id + "-delete"}>
                <Tooltip title={title[1]}>
                  <Button onClick={() => handleSetDeleteInfo(props.rowElement)}>
                    <Delete color="action" />
                  </Button>
                </Tooltip>
              </TableCell>
            </Hidden>
          )}
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 4 }}>
                <Typography variant="h6">{t("Users")}</Typography>
                {props.rowElement.users.map((user) => {
                  return (
                    <Typography key={user.id} variant="body1">
                      {user.name};
                    </Typography>
                  );
                })}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <Box>
      <AddGroupDialog
        users={props.users.filter((user) => user.status !== "ADMIN")}
        selectedGroup={selectedGroup}
        update={true}
        open={openUpdateGroupDialog}
        setOpen={setOpenUpdateGroupDialog}
      />
      {deletedGroup && (
        <DeleteElementFromTable
          open={openDelete}
          setOpen={setOpenDelete}
          entity={"group"}
          id={deletedGroup.id}
          name={deletedGroup.name}
          groupArray={props.groups}
        />
      )}
      <TableContainer component={Paper}>
        <Table sx={{ width: "100%" }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell
                align="center"
                width={localStorage.getItem("Role") === "USER" ? "50%" : "25%"}
              >
                {groupColumns[0]}
              </TableCell>
              <TableCell
                align="center"
                width={localStorage.getItem("Role") === "USER" ? "50%" : "25%"}
              >
                {groupColumns[1]}
              </TableCell>
              {localStorage.getItem("Role") !== "USER" && (
                <Hidden smDown>
                  <TableCell align="center">{groupColumns[2]}</TableCell>
                  <TableCell align="center">{groupColumns[3]}</TableCell>
                </Hidden>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading ? (
              skeletonArray.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    align="center"
                    width="20%"
                  >
                    <Skeleton />
                  </TableCell>
                  {localStorage.getItem("Role") !== "USER" && (
                    <Hidden smDown>
                      <TableCell align="center">
                        <Skeleton />
                      </TableCell>
                      <TableCell align="center">
                        <Skeleton />
                      </TableCell>
                    </Hidden>
                  )}
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                  <TableCell component="th" scope="row" align="center">
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))
            ) : props.groups.length > 0 ? (
              props.groups
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((element: Group) => (
                  <CustomTableRow rowElement={element} groups={props.groups} />
                ))
            ) : props.groups.length === 0 && !props.isError ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      height: "40vh",
                      alignItems: "center",
                    }}
                  >
                    <Stack justifyContent="center" alignItems="center">
                      <GroupRounded sx={{ fontSize: 40, color: grey[500] }} />
                      <NoDataToBeShown />
                      <Typography>
                        {t("Create a new group to add your apps to.")}
                      </Typography>
                    </Stack>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              props.isError && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        height: "40vh",
                        alignItems: "center",
                      }}
                    >
                      <Stack justifyContent="center" alignItems="center">
                        <ErrorComponent />
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        labelRowsPerPage={t("Rows per page")}
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={props.groups.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

export default GroupsTable;
